import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

/**
 * OWNED BY C1. Full-screen or bottom sheet. Used for the modifier sheet (F-304), tender screens,
 * the register picker, and every Setup sub-editor. Escape and the scrim close it.
 */
export interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  variant?: "full" | "bottom";
  /** Right-hand slot in the header. */
  action?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  /** Hide the close button when the sheet is a required decision. */
  dismissible?: boolean;
}

export function Sheet({ open, title, onClose, variant = "full", action, footer, children, dismissible = true }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || !dismissible) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, dismissible]);

  useEffect(() => {
    if (open) panel.current?.focus();
  }, [open]);

  if (!open) return null;
  return (
    <div className={`wc-sheet wc-sheet--${variant}`}>
      {dismissible ? <button type="button" className="wc-sheet__scrim" aria-label="Close" onClick={onClose} /> : <div className="wc-sheet__scrim" />}
      <div className="wc-sheet__panel" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={panel}>
        <header className="wc-sheet__head">
          {dismissible ? (
            <button type="button" className="wc-btn wc-btn--ghost" onClick={onClose} aria-label="Back">←</button>
          ) : null}
          <h2 className="wc-sheet__title">{title}</h2>
          {action}
        </header>
        <div className="wc-sheet__body">{children}</div>
        {footer ? <footer className="wc-sheet__foot">{footer}</footer> : null}
      </div>
    </div>
  );
}
